/**
 * QUOTATION FILE API — Wrapper dei REST pubblicati da
 * `QuotationFilesResourceImpl` (modulo crm-rest). Risorsa annidata sotto il
 * preventivo, con POST e PATCH in `multipart/form-data` perché trasportano il
 * binario.
 *
 * Contratto backend (rest-openapi.yaml + QuotationFilesResourceImpl):
 *   - GET    /quotation/{quotationId}/file                     -> Page<QuotationFile>
 *   - POST   /quotation/{quotationId}/file    multipart        -> QuotationFile
 *   - GET    /quotation/{quotationId}/file/{quotationFileId}   -> QuotationFile
 *   - PATCH  /quotation/{quotationId}/file/{quotationFileId}   multipart -> QuotationFile
 *   - DELETE /quotation/{quotationId}/file/{quotationFileId}   -> 200
 *
 * Niente parametro `search`: il backend filtra solo per `quotationId`, via
 * finder. La GET di lista ritorna una `Page<T>` (wrapper Vulcan trasparente)
 * anche se l'OpenAPI dichiara `type: array`.
 *
 * Il contenuto binario non compare mai nella response: si scarica dal
 * `fileEntryId` con le Headless DL API di Liferay.
 *
 * Questa risorsa NON usa `createCrudThunks`: il factory presuppone
 * `create(groupId, payload)` e `getById(id)`, mentre qui ogni operazione è
 * scopata sul preventivo padre e l'upload passa da `FormData`. Forzarla nel
 * factory avrebbe voluto dire piegare il contratto del factory a un solo caso.
 */

import type { Page } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import {
  quotationFileFromDto,
  buildQuotationFileUploadFormData,
  buildQuotationFileUpdateFormData,
  type QuotationFileDto,
  type QuotationFile,
  type QuotationFileType,
} from './quotationFileMappers';
import type { QuotationFileListParams } from './quotationFileTypes';

const BASE = '/quotation';

export const quotationFileApi = {
  getByQuotation: (
    quotationId: number,
    params?: QuotationFileListParams,
  ): Promise<Page<QuotationFile>> =>
    httpClient
      .get<Page<QuotationFileDto>>(`${BASE}/${quotationId}/file`, {
        params: params as Record<string, unknown> | undefined,
      })
      .then((r) => ({
        ...r.data,
        items: r.data.items.map(quotationFileFromDto),
      })),

  getById: (quotationId: number, quotationFileId: number): Promise<QuotationFile> =>
    httpClient
      .get<QuotationFileDto>(`${BASE}/${quotationId}/file/${quotationFileId}`)
      .then((r) => quotationFileFromDto(r.data)),

  upload: (
    quotationId: number,
    payload: { type: QuotationFileType; fileName: string; file: File | Blob },
  ): Promise<QuotationFile> =>
    httpClient
      .post<QuotationFileDto>(
        `${BASE}/${quotationId}/file`,
        buildQuotationFileUploadFormData(payload),
      )
      .then((r) => quotationFileFromDto(r.data)),

  update: (
    quotationId: number,
    quotationFileId: number,
    payload: { type: QuotationFileType; fileName?: string; file?: File | Blob },
  ): Promise<QuotationFile> =>
    httpClient
      .patch<QuotationFileDto>(
        `${BASE}/${quotationId}/file/${quotationFileId}`,
        buildQuotationFileUpdateFormData(payload),
      )
      .then((r) => quotationFileFromDto(r.data)),

  remove: (quotationId: number, quotationFileId: number): Promise<void> =>
    httpClient.delete(`${BASE}/${quotationId}/file/${quotationFileId}`).then(() => undefined),
};
